import createClient, { Client } from "openapi-fetch";

import { ICandyApiClient } from "../../../Common/CandyApiClient/ICandyApiClient";
import { reject } from "../../../Common/TypingUtils";
import { FormSettings } from "../Forms/Types/FormSettings";

import { paths as contractorsWidgetApiPaths } from "./Types/ContractorsWidgetSchema";
import { paths as contractorsServiceApiPaths } from "./Types/ContractorsServiceSchema";
import { WorkspaceResourceType } from "./Types/WorkspaceTypes";
import {
    CreateContractorRequestBody,
    GetContractorResponse,
    GetContractorsListQuery,
    GetContractorsResponse,
    SearchContractorsRequestBody,
    SearchContractorsResponse,
    SendFeedbackRequestBody,
    UpdateContractorRequestBody,
} from "./Types/ContractorsTypes";

export interface IContractorsApiClient {
    init(orgId: string, inn: string, resourceType: WorkspaceResourceType, kpp?: string): Promise<void>;

    getContractorsApiClient(): Client<contractorsWidgetApiPaths>;
    getDirectoryId(): string | null;

    getContractors(query?: GetContractorsListQuery): Promise<GetContractorsResponse>;
    createContractor(body: CreateContractorRequestBody): Promise<GetContractorResponse>;

    searchContractors(body: SearchContractorsRequestBody): Promise<SearchContractorsResponse>;

    getContractor(
        contractorId: string,
        query?: {
            customAddressType?: string | null;
        }
    ): Promise<GetContractorResponse>;
    deleteContractor(contractorId: string): Promise<void>;
    updateContractor(contractorId: string, body: UpdateContractorRequestBody): Promise<GetContractorResponse>;

    sendFeedback(body: SendFeedbackRequestBody): Promise<void>;
}

type ContractorsApiClientOptions = Pick<FormSettings, "contractorsApiUrl">;

export class ContractorsApiClient implements IContractorsApiClient {
    private readonly contractorsWidgetApiClient: Client<contractorsWidgetApiPaths>;
    private readonly contractorsServiceApiClient: Client<contractorsServiceApiPaths>;
    private readonly candyApiClient: ICandyApiClient;
    private directoryId: string | null = null;

    public constructor(candyApiClient: ICandyApiClient, options: ContractorsApiClientOptions) {
        this.contractorsWidgetApiClient = createClient<contractorsWidgetApiPaths>({
            baseUrl: options.contractorsApiUrl,
            credentials: "include",
        });
        this.contractorsServiceApiClient = createClient<contractorsServiceApiPaths>({
            baseUrl: options.contractorsApiUrl,
            credentials: "include",
        });
        this.candyApiClient = candyApiClient;
    }

    public async init(orgId: string, inn: string, resourceType: WorkspaceResourceType, kpp?: string): Promise<void> {
        if (!this.directoryId) {
            this.directoryId = await this.candyApiClient.getWorkspaceDirectoryId(orgId, inn, resourceType, kpp);
        }
    }

    public getContractorsApiClient(): Client<contractorsWidgetApiPaths> {
        return this.contractorsWidgetApiClient;
    }

    public getDirectoryId(): string | null {
        return this.directoryId;
    }

    public async getContractors(query?: GetContractorsListQuery): Promise<GetContractorsResponse> {
        const directoryId = this.directoryId ?? reject("directoryId is not defined");

        const response = await this.contractorsWidgetApiClient.GET("/widget/v1/directories/{directoryId}/contractors", {
            params: { path: { directoryId: directoryId }, query: query },
        });

        if (response.error) {
            throw new Error(response.error.error.message || response.error.statusCode.toString());
        }

        return response.data;
    }

    public async createContractor(body: CreateContractorRequestBody): Promise<GetContractorResponse> {
        const directoryId = this.directoryId ?? reject("directoryId is not defined");

        const response = await this.contractorsWidgetApiClient.POST(
            "/widget/v1/directories/{directoryId}/contractors",
            {
                params: { path: { directoryId: directoryId } },
                body: body,
            }
        );

        if (response.error) {
            throw new Error(response.error.error.message || response.error.statusCode.toString());
        }

        return response.data;
    }

    public async searchContractors(body: SearchContractorsRequestBody): Promise<SearchContractorsResponse> {
        const directoryId = this.directoryId ?? reject("directoryId is not defined");

        const response = await this.contractorsServiceApiClient.POST(
            "/service/v1/directories/{directoryId}/contractors/search",
            {
                params: { path: { directoryId: directoryId } },
                body: body,
            }
        );

        if (response.error) {
            throw new Error(response.error.error.message || response.error.statusCode.toString());
        }

        return response.data;
    }

    public async getContractor(
        contractorId: string,
        query?: {
            customAddressType?: string | null;
        }
    ): Promise<GetContractorResponse> {
        const directoryId = this.directoryId ?? reject("directoryId is not defined");

        const response = await this.contractorsWidgetApiClient.GET(
            "/widget/v1/directories/{directoryId}/contractors/{contractorId}",
            {
                params: { path: { directoryId: directoryId, contractorId: contractorId }, query: query },
            }
        );

        if (response.error) {
            throw new Error(response.error.error.message || response.error.statusCode.toString());
        }

        return response.data;
    }

    public async deleteContractor(contractorId: string): Promise<void> {
        const directoryId = this.directoryId ?? reject("directoryId is not defined");

        const response = await this.contractorsWidgetApiClient.DELETE(
            "/widget/v1/directories/{directoryId}/contractors/{contractorId}",
            {
                params: { path: { directoryId: directoryId, contractorId: contractorId } },
            }
        );

        if (response.error) {
            throw new Error(response.error.error.message || response.error.statusCode.toString());
        }
    }

    public async updateContractor(
        contractorId: string,
        body: UpdateContractorRequestBody
    ): Promise<GetContractorResponse> {
        const directoryId = this.directoryId ?? reject("directoryId is not defined");

        const response = await this.contractorsWidgetApiClient.PATCH(
            "/widget/v1/directories/{directoryId}/contractors/{contractorId}",
            {
                params: { path: { directoryId: directoryId, contractorId: contractorId } },
                body: body,
            }
        );

        if (response.error) {
            throw new Error(response.error.error.message || response.error.statusCode.toString());
        }

        return response.data;
    }

    public async sendFeedback(body: SendFeedbackRequestBody): Promise<void> {
        const directoryId = this.directoryId ?? reject("directoryId is not defined");

        const response = await this.contractorsWidgetApiClient.POST("/widget/v1/directories/{directoryId}/feedback", {
            params: { path: { directoryId: directoryId } },
            body: body,
        });

        if (response.error) {
            throw new Error(response.error.error.message || response.error.statusCode.toString());
        }
    }
}
